import { useEffect, useState, type FormEvent } from "react";

import {
  loadOutboundSendConfiguration,
  updateOutboundSendConfiguration,
} from "./api";
import "./configuration.css";
import { CaseMetricConfiguration } from "./CaseMetricConfiguration";

export function BasicConfiguration({
  workspaceName,
  canConfigureMail = true,
  canConfigureMetrics = true,
}: {
  workspaceName?: string;
  canConfigureMail?: boolean;
  canConfigureMetrics?: boolean;
}) {
  const [tab, setTab] = useState(canConfigureMail ? "mail" : "metrics");
  const [defaultBccEmail, setDefaultBccEmail] = useState("");
  const [savedBccEmail, setSavedBccEmail] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");

  useEffect(() => {
    if (!canConfigureMail) return;
    const controller = new AbortController();
    void loadOutboundSendConfiguration(controller.signal)
      .then((configuration) => {
        if (controller.signal.aborted) return;
        setDefaultBccEmail(configuration.defaultBccEmail);
        setSavedBccEmail(configuration.defaultBccEmail);
      })
      .catch((cause) => {
        if (!controller.signal.aborted)
          setError(cause instanceof Error ? cause.message : String(cause));
      })
      .finally(() => {
        if (!controller.signal.aborted) setLoading(false);
      });
    return () => controller.abort();
  }, [canConfigureMail]);

  async function save(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (saving) return;
    setSaving(true);
    setError("");
    setNotice("");
    try {
      const updated = await updateOutboundSendConfiguration(
        defaultBccEmail.trim(),
      );
      setDefaultBccEmail(updated.defaultBccEmail);
      setSavedBccEmail(updated.defaultBccEmail);
      setNotice("发件配置已保存");
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : String(cause));
    } finally {
      setSaving(false);
    }
  }

  return (
    <section className="basic-configuration-page">
      <header className="basic-configuration-header">
        <div>
          <h1>基础配置</h1>
          <p>管理当前工作空间的通用业务配置。</p>
        </div>
      </header>
      <nav className="basic-configuration-tabs" role="tablist">
        {canConfigureMail && (
          <button
            type="button"
            role="tab"
            aria-selected={tab === "mail"}
            className={tab === "mail" ? "active" : ""}
            onClick={() => setTab("mail")}
          >
            发件配置
          </button>
        )}
        {canConfigureMetrics && (
          <button
            type="button"
            role="tab"
            aria-selected={tab === "metrics"}
            className={tab === "metrics" ? "active" : ""}
            onClick={() => setTab("metrics")}
          >
            自定义案件指标
          </button>
        )}
      </nav>
      {tab === "metrics" && canConfigureMetrics ? (
        <CaseMetricConfiguration workspaceName={workspaceName} />
      ) : null}
      <div
        className="basic-configuration-content"
        role="tabpanel"
        hidden={tab !== "mail" || !canConfigureMail}
      >
        <div className="basic-configuration-section-heading">
          <h2>配置密送邮箱</h2>
          <p>
            当前工作空间：{workspaceName || "当前空间"}
            。此处配置仅对该空间生效，其他空间请切换后分别设置。
          </p>
        </div>
        {loading ? (
          <p className="basic-configuration-status">正在加载配置…</p>
        ) : (
          <form onSubmit={(event) => void save(event)}>
            <label htmlFor="default-bcc-email">默认密送收件人</label>
            <input
              id="default-bcc-email"
              type="email"
              maxLength={320}
              value={defaultBccEmail}
              placeholder="例如：archive@example.com"
              onChange={(event) => {
                setDefaultBccEmail(event.target.value);
                setNotice("");
              }}
            />
            <small>
              留空时不密送。保存后，案件对外发信会自动带入此地址，发件人仍可在发送前编辑。
            </small>
            <div className="basic-configuration-actions">
              <button
                type="submit"
                className="basic-configuration-primary"
                disabled={saving || defaultBccEmail.trim() === savedBccEmail}
              >
                {saving ? "正在保存…" : "保存配置"}
              </button>
            </div>
          </form>
        )}
        {error ? <p className="basic-configuration-error">{error}</p> : null}
        {notice ? (
          <p className="basic-configuration-notice" role="status">
            {notice}
          </p>
        ) : null}
      </div>
    </section>
  );
}
